
import { supabase } from '@/services/supabase/supabase';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useProjectStore } from './useProjectStore';

export interface ProjectMeta {
  id: string;
  name: string;
  description?: string;
  genre?: string;
  ownerId?: string;
  currentStoryId?: string;
  createdAt: string;
  updatedAt: string;
  isLoading: boolean;
  error: Error | null;
}

export const projectMetaKeys = {
  detail: (projectId: string) => ['meta', 'project', projectId]
};

export function useProjectMeta(projectId: string | null) {
  const setProjectMeta = useProjectStore((state) => state.setProjectMeta);

  const { data, isLoading, error } = useQuery({
    queryKey: projectMetaKeys.detail(projectId || ''),
    queryFn: async () => {
      if (!projectId) return null;

      const { data, error } = await supabase
        .from('projects')
        .select('id, name, description, genre, current_story_id, created_at, updated_at')
        .eq('id', projectId)
        .single();

      if (error) throw error;

      return {
        id: data.id,
        name: data.name,
        description: data.description,
        genre: data.genre,
        currentStoryId: data.current_story_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      } as ProjectMeta;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const result = {
    id: data?.id,
    name: data?.name,
    description: data?.description,
    genre: data?.genre,
    ownerId: data?.ownerId,
    currentStoryId: data?.currentStoryId,
    createdAt: data?.createdAt,
    updatedAt: data?.updatedAt,
    isLoading,
    error: error as Error | null
  };

  useEffect(() => {
    setProjectMeta(result);
  }, [data, isLoading, error, setProjectMeta]);

  return result;
}

